import { createRouter, createWebHistory } from 'vue-router'
import Register from '../views/Register.vue'
import Login from '../views/Login.vue'
import Home from '../views/Home.vue'
import PatientHome from '../views/PatientHome.vue'
import DoctorSchedule from '../views/DoctorSchedule.vue'
import Profile from '../views/Profile.vue'
import Patients from '../views/Patients.vue'
import Appointments from '../views/Appointments.vue'
import HealthTracking from '../views/HealthTracking.vue'
import BiometricRegister from '../views/BiometricRegister.vue'
import NurseCallsHistory from '../views/NurseCallsHistory.vue'
import PrivacyPolicy from '../views/PrivacyPolicy.vue'
import UserAgreement from '../views/UserAgreement.vue'
import LicenseAgreement from '../views/LicenseAgreement.vue'
import ServiceTerms from '../views/ServiceTerms.vue'
import ChangePassword from '../views/ChangePassword.vue'
import api from '../services/api'

// Кеширование данных пользователя для улучшения производительности роутера
let cachedUser = null
let lastAuthCheck = 0
const AUTH_CACHE_DURATION = 30 * 1000 // 30 секунд

async function getCachedUser() {
  const now = Date.now()

  // Если кеш актуален, используем его
  if (cachedUser && (now - lastAuthCheck) < AUTH_CACHE_DURATION) {
    return cachedUser
  }

  try {
    const response = await api.checkAuth()
    if (response.data.isAuthenticated) {
      cachedUser = response.data.user
      lastAuthCheck = now
      return cachedUser
    } else {
      cachedUser = null
      return null
    }
  } catch (error) {
    cachedUser = null
    return null
  }
}

// Функция для сброса кеша (вызывается при logout)
function clearUserCache() {
  cachedUser = null
  lastAuthCheck = 0
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/patient-home',
    name: 'PatientHome',
    component: PatientHome,
    meta: { requiresAuth: true, requiresPatient: true }
  },
  {
    path: '/register',
    name: 'RegisterForm',
    component: Register
  },
  {
    path: '/biometric-register',
    name: 'BiometricRegister',
    component: BiometricRegister
  },
  {
    path: '/login',
    name: 'Login',
    component: Login
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('../views/ForgotPassword.vue')
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/ResetPassword.vue')
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: ChangePassword,
    meta: { requiresAuth: true },
    props: route => ({
      isMandatory: route.query.mandatory === 'true',
      isPasswordExpired: route.query.expired === 'true'
    })
  },
  {
    path: '/doctor-schedule',
    name: 'DoctorSchedule',
    component: DoctorSchedule,
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/patients',
    name: 'Patients',
    component: Patients,
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/organization-patients',
    name: 'OrganizationPatients',
    component: () => import('../views/OrganizationPatients.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/detected-faces',
    name: 'DetectedFaces',
    component: () => import('../views/DetectedFaces.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/appointments',
    name: 'Appointments',
    component: Appointments,
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/clinic-appointments',
    name: 'ClinicAppointments',
    component: Appointments,
    props: { mode: 'manager' },
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    path: '/clinic-schedule',
    name: 'ClinicSchedule',
    component: DoctorSchedule,
    props: { mode: 'manager' },
    meta: { requiresAuth: true, requiresClinicManager: true }
  },
  {
    // Психомониторинг для тестового врача (использует DoctorLayout)
    path: '/psychological-monitoring',
    name: 'DoctorPsychologicalMonitoring',
    component: () => import('../views/staff/PsychologicalRiskDashboard.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/health-tracking',
    name: 'HealthTracking',
    component: HealthTracking,
    meta: { requiresAuth: true }
  },
  {
    path: '/nurse-calls-history',
    name: 'NurseCallsHistory',
    component: NurseCallsHistory,
    meta: { requiresAuth: true, requiresNurse: true }
  },
  {
    path: '/patients/:id',
    name: 'PatientDetails',
    component: () => import('../views/PatientDetails.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/patients/:patientId/tracker',
    name: 'PatientTracker',
    component: () => import('../views/PatientTracker.vue'),
    meta: { requiresAuth: true }
  },

  // Панель персонала (для is_staff)
  {
    path: '/staff',
    name: 'StaffDashboard',
    component: () => import('../views/StaffDashboard.vue'),
    meta: { requiresAuth: true, requiresStaff: true }
  },
  {
    path: '/staff/locations',
    name: 'StaffLocations',
    component: () => import('../views/StaffLocations.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/cities',
    name: 'StaffCities',
    component: () => import('../views/StaffCities.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/medical-staff',
    name: 'StaffMedical',
    component: () => import('../views/StaffMedical.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/patients',
    name: 'StaffPatients',
    component: () => import('../views/StaffPatients.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/users',
    name: 'StaffUsersManagement',
    component: () => import('../views/StaffUsersManagement.vue'),
    meta: { requiresAuth: true, requiresStaff: true, chiefDoctorAccess: true }
  },
  {
    path: '/staff/personnel',
    name: 'StaffPersonnelManagement',
    redirect: '/staff/users?tab=personnel'
  },
  {
    path: '/staff/specializations',
    name: 'StaffSpecializations',
    component: () => import('../views/StaffSpecializations.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/health-trackers',
    name: 'StaffHealthTrackers',
    component: () => import('../views/StaffHealthTrackers.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/visit-reasons',
    name: 'StaffVisitReasons',
    component: () => import('../views/StaffVisitReasons.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/logging',
    name: 'StaffLogging',
    component: () => import('../views/StaffLogging.vue'),
    meta: { requiresAuth: true, requiresStaff: true, chiefDoctorAccess: true }
  },
  {
    path: '/staff/org-reports',
    name: 'StaffOrgReports',
    component: () => import('../views/StaffOrgReports.vue'),
    meta: { requiresAuth: true, requiresStaff: true, chiefDoctorAccess: true }
  },
  {
    path: '/staff/support',
    name: 'StaffSupport',
    component: () => import('../views/staff/StaffSupport.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/support/:ticketId',
    name: 'StaffSupportChat',
    component: () => import('../views/staff/StaffSupportChat.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/surveys',
    name: 'StaffSurveys',
    component: () => import('../views/StaffSurveys.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/organizations',
    name: 'StaffOrganizations',
    component: () => import('../views/StaffOrganizations.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/surveys/create',
    name: 'SurveyBuilder',
    component: () => import('../views/staff/SurveyBuilder.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/surveys/:id/edit',
    name: 'SurveyEdit',
    component: () => import('../views/staff/SurveyBuilder.vue'),
    meta: { requiresAuth: true, requiresStaff: true, staffOnly: true }
  },
  {
    path: '/staff/surveys/:id/statistics',
    name: 'SurveyStatistics',
    component: () => import('../views/staff/SurveyStatistics.vue'),
    meta: { requiresAuth: true, requiresStaff: true, chiefDoctorAccess: true }
  },
  {
    path: '/staff/psychological-monitoring',
    name: 'PsychologicalRiskDashboard',
    component: () => import('../views/staff/PsychologicalRiskDashboard.vue'),
    meta: { requiresAuth: true }  // Проверка доступа идёт в sidebar и компоненте
  },
  // Дэшборд для авторизованных пользователей
  {
    path: '/dashboard/almaty',
    name: 'DashboardPage',
    component: () => import('../views/Dashboard.vue'),
    meta: { requiresAuth: true }
  },
  // Документы - доступны всем
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy
  },

  {
    path: '/user-agreement',
    name: 'UserAgreement',
    component: UserAgreement
  },
  {
    path: '/license-agreement',
    name: 'LicenseAgreement',
    component: LicenseAgreement
  },
  {
    path: '/service-terms',
    name: 'ServiceTerms',
    component: ServiceTerms
  },
  {
    path: '/doctor-manual',
    name: 'DoctorManual',
    component: () => import('../views/DoctorManual.vue')
  },
  {
    path: '/user-manual',
    name: 'UserManual',
    component: () => import('../views/UserManual.vue')
  },
  // PDF прямого просмотра для мобильных приложений
  {
    path: '/pdf/:type/:lang',
    name: 'PdfViewer',
    component: () => import('../views/PdfViewer.vue'),
    props: true
  },
  {
    path: '/certificate',
    name: 'Certificate',
    component: () => import('../views/Certificate.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  // Техподдержка - доступна только врачам
  {
    path: '/support',
    name: 'SupportPage',
    component: () => import('../views/Support.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  },
  {
    path: '/support/:ticketId',
    name: 'SupportChat',
    component: () => import('../views/SupportChat.vue'),
    meta: { requiresAuth: true, requiresDoctor: true }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Utility function to check if user has a specific role
const hasRole = (userTypes, role) => {
  return Array.isArray(userTypes) && userTypes.includes(role)
}

// Navigation guard
router.beforeEach(async (to, from, next) => {

  // Skip authentication check for public pages
  if (to.path === '/register' || to.path === '/login' || to.path === '/biometric-register' ||
    to.path === '/privacy-policy' || to.path === '/user-agreement' ||
    to.path === '/license-agreement' || to.path === '/service-terms' ||
    to.path === '/doctor-manual' || to.path === '/user-manual' ||
    to.path.startsWith('/pdf/')) {
    next()
    return
  }

  // If the route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    try {
      // Check if user is logged in using cached data
      const user = await getCachedUser()

      if (!user) {
        // Redirect to login page with return URL
        next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
        return
      }
      const userType = user?.type        // Основная роль для обратной совместимости
      const userTypes = user?.types || [] // Все роли пользователя

      // ПЕРВАЯ ПРОВЕРКА: Админские маршруты (только для is_superuser)
      if (to.matched.some(record => record.meta.requiresAdmin)) {
        if (!user.is_superuser) {
          // Редирект в зависимости от прав пользователя
          if (userType === 'staff') {
            next('/staff')  // Персонал идет на свою панель
          } else if (userType === 'doctor' || userType === 'nurse') {
            next('/')
          } else if (userType === 'patient') {
            next('/patient-home')
          } else {
            next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
          }
          return
        } else {
          next()
          return
        }
      }

      // ВТОРАЯ ПРОВЕРКА: Маршруты персонала (для is_staff, но не is_superuser)
      if (to.matched.some(record => record.meta.requiresStaff)) {
        // Проверяем, разрешён ли доступ тестовому врачу
        const allowTestDoctor = to.matched.some(record => record.meta.allowTestDoctor)
        const userName = user.name || ''
        const isTestDoctor = userName.includes('Сембинова') && userName.includes('Алтынай')

        if (!user.is_staff && !(allowTestDoctor && isTestDoctor)) {
          if (userType === 'doctor' || userType === 'nurse') {
            next('/')
          } else if (userType === 'patient') {
            next('/patient-home')
          } else {
            next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
          }
          return
        }

        // ДОПОЛНИТЕЛЬНАЯ ПРОВЕРКА: Ограничения для ролей с ограниченным доступом
        const userStaffRole = user.staff_role || 'staff'
        if (userStaffRole === 'clinic_manager') {
          next('/clinic-appointments')
          return
        }
        const isOrgDirector = ['org_director', 'observer'].includes(userStaffRole)
        const isChiefDoctor = ['chief_doctor', 'org_director', 'observer'].includes(userStaffRole)
        const isClinicAdmin = userStaffRole === 'clinic_admin'
        const hasLimitedAccess = isChiefDoctor || isClinicAdmin

        if (to.name === 'StaffOrgReports' && !isOrgDirector) {
          next('/staff/logging')
          return
        }

        // Для директора организации и обозревателя закрываем доступ к старой странице отчетов и статистике опросов
        if (isOrgDirector && to.name === 'StaffLogging') {
          next('/staff/org-reports')
          return
        }
        if (isOrgDirector && to.name === 'SurveyStatistics') {
          next('/staff/org-reports')
          return
        }

        // Страницы только для обычного персонала (недоступны для Главного врача и Администратора учреждения)
        if (to.matched.some(record => record.meta.staffOnly) && hasLimitedAccess) {
          next('/staff/users')  // Редирект на страницу пользователей
          return
        }

        // Страницы, доступные для персонала, Главного врача и Администратора учреждения
        if (to.matched.some(record => record.meta.chiefDoctorAccess) ||
          !to.matched.some(record => record.meta.staffOnly)) {
          next()
          return
        }

        // Обычный персонал имеет доступ ко всем страницам
        if (!hasLimitedAccess) {
          next()
          return
        }
      }

      // Redirect to appropriate home page based on user type
      if (to.path === '/') {
        if (userType === 'staff') {
          if (user.staff_role === 'clinic_manager') {
            next('/clinic-appointments')
          } else {
            next('/staff')
          }
          return
        } else if (userType === 'doctor' || userType === 'nurse') {
          // For doctors and nurses, allow access to home page
          // Note: Schedule check temporarily disabled to allow normal navigation
          next()
          return
        } else if (userType === 'patient') {
          next('/patient-home')
          return
        } else {
          // Пользователь без типа - проверяем права
          if (user.is_superuser) {
            next('/staff')
            return
          } else if (user.is_staff) {
            next('/staff')
            return
          } else {
            next('/login')
            return
          }
        }
      }

      // If the route requires clinic manager role
      if (to.matched.some(record => record.meta.requiresClinicManager)) {
        const isClinicManager = user.is_staff && user.staff_role === 'clinic_manager'

        if (!isClinicManager) {
          if (user.is_staff || hasRole(userTypes, 'staff')) {
            next('/staff')
          } else if (hasRole(userTypes, 'doctor') || hasRole(userTypes, 'nurse')) {
            next('/')
          } else if (hasRole(userTypes, 'patient')) {
            next('/patient-home')
          } else {
            next('/login')
          }
          return
        }
      }

      // If the route requires doctor role (allow both doctor and nurse)
      if (to.matched.some(record => record.meta.requiresDoctor)) {
        if (!hasRole(userTypes, 'doctor') && !hasRole(userTypes, 'nurse')) {
          // Not a doctor or nurse, redirect to appropriate home
          if (user.is_staff || hasRole(userTypes, 'staff')) {
            next('/staff')
          } else if (hasRole(userTypes, 'patient')) {
            next('/patient-home')
          } else {
            next('/login')
          }
          return
        }
      }

      // If the route requires nurse role
      if (to.matched.some(record => record.meta.requiresNurse)) {
        if (!hasRole(userTypes, 'nurse')) {
          // Not a nurse, redirect to appropriate home
          if (hasRole(userTypes, 'doctor')) {
            next('/')
          } else if (user.is_staff || hasRole(userTypes, 'staff')) {
            next('/staff')
          } else if (hasRole(userTypes, 'patient')) {
            next('/patient-home')
          } else {
            next('/login')
          }
          return
        }
      }

      // If the route requires patient role
      if (to.matched.some(record => record.meta.requiresPatient)) {
        if (!hasRole(userTypes, 'patient')) {
          // Not a patient, redirect to appropriate home
          if (user.is_staff || hasRole(userTypes, 'staff')) {
            next('/staff')
          } else if (hasRole(userTypes, 'doctor') || hasRole(userTypes, 'nurse')) {
            next('/')
          } else {
            next('/login')
          }
          return
        }
      }

      next()
    } catch (error) {
      next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
    }
  } else {
    next()
  }
})

export default router
export { clearUserCache } 
